import * as React from 'react'
import * as classNames from 'classnames'
import { Field, FieldProps, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { Button, Input, Select } from '@mobiscroll/react-latest'
import { IBankAccountObj, ICreateBankAccountObj, IMap, IOrgObj } from '../../../../types'
import UIMessages from '../../../../utils/ui-messages'
import { isNotBlank, isNumberValid } from '../../../../utils/validators'
import ErrorMessage from '../../../ui/ErrorMessage'
import CreateOrganizationBottomBar from '../CreateOrganizationBottomBar'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import { accountHolderTypeOptions, countryOptions, currencyOptions } from 'src/constants/create-organization-form'
import BankAccountListItem from './BankAccountListItem'

export interface IFormValues {
  bankAccounts?: IMap<IBankAccountObj>
  formError?: string
  accountHolderType: string
  accountHolderName: string
  accountNumber: string
  country: string
  currency: string
  routingNumber: string
  connectURL: string | undefined
  match: string
  isBankAccountAdded?: boolean
  isLegalEntityCompleted?: boolean
  onSetPrimaryBankAccount: (bankAccountId: string) => Promise<any>
  showMessage: (error: string, isError: boolean) => void
}

interface IFormProps {
  connectURL: string | undefined
  bankAccount: ICreateBankAccountObj
  bankAccounts?: IMap<IBankAccountObj>
  isSaving: boolean
  isEdit?: boolean
  orgId: string
  organization: IOrgObj
  match: string
  isBankAccountAdded?: boolean
  isLegalEntityCompleted?: boolean
  onSetPrimaryBankAccount: (bankAccountId: string) => Promise<any>
  showMessage: (error: string, isError: boolean) => void
}

interface IBankAccountFormProps {
  connectURL: string | undefined
  bankAccount: ICreateBankAccountObj
  bankAccounts?: IMap<IBankAccountObj>
  currentStep: number
  isSaving: boolean
  isCompleted: boolean
  organization: IOrgObj
  isEdit?: boolean
  isBankAccountAdded?: boolean
  isLegalEntityCompleted?: boolean
  orgId: string
  match: string
  onCompleteStep: (step: number) => void
  onSaveBankAccount: (bankAccount: ICreateBankAccountObj) => Promise<any>
  onSetPrimaryBankAccount: (bankAccountId: string) => Promise<any>
  showMessage: (error: string, isError: boolean) => void
}

const routingNumberLength = 9

export const InnerForm = (props: IFormProps & FormikProps<IFormValues>): any => {
  const {
    bankAccounts,
    connectURL,
    errors,
    isEdit,
    isSaving,
    isSubmitting,
    organization,
    orgId,
    touched,
    values,
    match,
    isBankAccountAdded,
    isLegalEntityCompleted,
    onSetPrimaryBankAccount,
    setFieldTouched,
    setFieldValue,
    showMessage
  } = props

  const { accountHolderType, country, currency } = values
  const sortedBankAccts = bankAccounts
    ? Object.values(bankAccounts).sort((a, b) => (b.default_for_currency ? 1 : 0) - (a.default_for_currency ? 1 : 0))
    : []

  const inputProps = {
    inputStyle: 'box' as const,
    labelStyle: 'floating' as const,
    theme: 'ios'
  }

  const select = (value: FieldProps<IFormValues>) => {
    const { field } = value
    const onChangeWrap = (value: string) => {
      setFieldTouched(field.name)
      setFieldValue(field.name, value)
    }

    const inputDetails = {
      accountHolderType: {
        data: accountHolderTypeOptions,
        defaultValue: accountHolderType,
        filter: false,
        label: `${UIMessages.organization.bankAccount.accountHolderType}*`,
        required: true
      },
      country: {
        data: countryOptions,
        defaultValue: country,
        filter: true,
        label: `${UIMessages.common.country}*`,
        required: true
      },
      currency: {
        data: currencyOptions,
        defaultValue: currency,
        filter: false,
        label: `${UIMessages.common.currency}*`,
        required: true
      }
    }
    const selectProps = inputDetails[field.name as keyof typeof inputDetails]
    return <Select {...field} {...inputProps} {...selectProps} onChange={({ value }) => onChangeWrap(value)} />
  }

  const textInput = ({ field }: FieldProps<IFormValues>) => {
    const inputDetails = {
      accountHolderName: {
        label: `${UIMessages.organization.bankAccount.accountHolderName}*`,
        required: true,
        type: 'text'
      },
      accountNumber: {
        label: `${UIMessages.organization.bankAccount.accountNumber}*`,
        required: true,
        type: 'text'
      },
      routingNumber: {
        label: `${UIMessages.organization.bankAccount.routingNumber}*`,
        required: true,
        type: 'text'
      }
    }
    const textInputProps = inputDetails[field.name as keyof typeof inputDetails]
    return <Input {...field} {...inputProps} {...textInputProps} />
  }

  const bodyStyle = classNames(
    { 'pointer-events-auto opacity1': !(isSaving || isSubmitting) },
    { 'pointer-events-none opacity04': isSaving || isSubmitting || (!isLegalEntityCompleted && !isEdit) }
  )

  const prevStepPath = isEdit ? `/organizations/${orgId}/edit/legal-entity` : '/organizations/create/legal-entity'
  const getFinishPath = (): string => {
    if (connectURL && (isBankAccountAdded && isLegalEntityCompleted)) {
      return connectURL
    }
    if (isEdit && !isBankAccountAdded && !isLegalEntityCompleted) {
      return `/organizations/${orgId}`
    }
    return ''
  }

  const disableFinishBtn = isEdit ? false : connectURL && (isBankAccountAdded && isLegalEntityCompleted) ? false : true

  !isLegalEntityCompleted && !isEdit
    ? showMessage(UIMessages.organization.bankAccount.validation.legalEntityRequired, true)
    : showMessage('', false)

  return (
    <Form className='org-bank-account-container'>
      <FloatingForm>
        <div className={bodyStyle}>
          <ErrorMessage visible={!!errors.formError} className='mt1'>
            {UIMessages.common.failedRiderCreate}
          </ErrorMessage>
          <div className='mbsc-grid' style={{ padding: '0' }}>
            <div className='mbsc-row'>
              <div className='mbsc-col-lg-8'>
                <p className='bold font-grey3'>{UIMessages.common.bankAccount}</p>
                <div className='bb-shadow-grey' />
                <div className='mbsc-row'>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='accountHolderType' render={select} />
                    {touched.accountHolderType && errors.accountHolderType && (
                      <span className='font-small weight-300 font-red block relative h0'>
                        {errors.accountHolderType}
                      </span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field className='AccountHolderNameField' name='accountHolderName' render={textInput} />
                    {touched.accountHolderName && errors.accountHolderName && (
                      <span className='ErrorMsgAccHolderNameDiv font-small weight-300 font-red block relative h0'>
                        {errors.accountHolderName}
                      </span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field className='AccountNumberField' name='accountNumber' render={textInput} />
                    {touched.accountNumber && errors.accountNumber && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.accountNumber}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field className='RoutingNumberField' name='routingNumber' render={textInput} />
                    {touched.routingNumber && errors.routingNumber && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.routingNumber}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='country' render={select} />
                    {touched.country && errors.country && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.country}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='currency' render={select} />
                    {touched.currency && errors.currency && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.currency}</span>
                    )}
                  </div>
                </div>
                <div className='mbsc-row'>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Button className='mbsc-success-btn' type='submit'>
                      {UIMessages.organization.bankAccount.addBankAccount}
                    </Button>
                  </div>
                </div>
                {/* CONNECTED BANK ACCOUNTS */}
                {sortedBankAccts && !!sortedBankAccts.length && (
                  <div>
                    <p className='bold font-grey3'>{UIMessages.organization.bankAccount.connectedBankAccounts}</p>
                    <div className='bb-shadow-grey' />
                    <div className='mbsc-row mb2 pt1'>
                      {Object.values(sortedBankAccts).map(bankAccount => (
                        <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding' key={bankAccount.id}>
                          <BankAccountListItem
                            onSetPrimaryBankAccount={onSetPrimaryBankAccount}
                            match={match}
                            bankAccount={bankAccount}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className='mbsc-col-lg-4'>
                <p className='bold font-grey3'>{UIMessages.common.tips}</p>
                <div className='bb-shadow-grey' />
                <div className='mbsc-row'>
                  <div className='mbsc-col-12 mbsc-padding'>
                    <div className='font-light-grey'>{UIMessages.common.bankAccountTips}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FloatingForm>
      <CreateOrganizationBottomBar
        isSaved={true}
        isSaving={isSubmitting}
        previousStepPath={prevStepPath}
        finishPath={connectURL ? connectURL : `/organizations/${orgId}`}
        disableFinishBtn={disableFinishBtn}
      />
    </Form>
  )
}

export const BankAccountForm = withFormik<IBankAccountFormProps, IFormValues>({
  mapPropsToValues: (props: IBankAccountFormProps): IFormValues => {
    const { accountHolderType, accountHolderName, accountNumber, routingNumber, country, currency } = props.bankAccount
    // CONNECTED BANK ACCOUNTS
    const { bankAccounts, match, isBankAccountAdded, isLegalEntityCompleted, onSetPrimaryBankAccount, showMessage } =
      props || undefined

    return {
      bankAccounts,
      connectURL: props.connectURL,
      accountHolderName,
      accountNumber,
      routingNumber,
      accountHolderType: accountHolderType ? accountHolderType : accountHolderTypeOptions[0].value,
      country: country ? country : countryOptions[0].value,
      currency: currency ? currency : currencyOptions[0].value,
      match,
      isBankAccountAdded,
      isLegalEntityCompleted,
      onSetPrimaryBankAccount,
      showMessage
    }
  },
  mapValuesToPayload: x => x,
  validate: (
    { accountHolderName, accountNumber, routingNumber }: IFormValues,
    { showMessage }
  ): FormikErrors<IFormValues> => {
    const errors: FormikErrors<IFormValues> = {}
    if (!isNotBlank(accountHolderName)) {
      errors.accountHolderName = UIMessages.organization.bankAccount.validation.accountHolderNameIsMissing
    }

    if (!isNotBlank(accountNumber)) {
      errors.accountNumber = UIMessages.organization.bankAccount.validation.accountNumberIsMissing
    }

    if (!isNumberValid(accountNumber)) {
      errors.accountNumber = UIMessages.organization.bankAccount.validation.accountNumberIsInvalid
    }

    if (!isNotBlank(routingNumber)) {
      errors.routingNumber = UIMessages.organization.bankAccount.validation.routingNumberIsMissing
    }

    if (!isNumberValid(routingNumber)) {
      errors.routingNumber = UIMessages.organization.bankAccount.validation.routingNumberIsInvalid
    } else if (routingNumber.length !== routingNumberLength) {
      errors.routingNumber = UIMessages.organization.bankAccount.validation.routingNumberMustHave9Digits
    }
    return errors
  },
  handleSubmit: (
    { accountHolderType, accountHolderName, accountNumber, routingNumber, country, currency }: IFormValues,
    { props, setSubmitting, resetForm }
  ) => {
    setSubmitting(true)
    props.onCompleteStep(3)
    props
      .onSaveBankAccount({
        ...props.bankAccount,
        accountHolderType,
        accountHolderName,
        accountNumber,
        routingNumber,
        country,
        currency,
        status: 'OK'
      })
      .then(() => {
        setSubmitting(false)
        resetForm({
          accountHolderName: '',
          accountHolderType: accountHolderTypeOptions[0].value,
          accountNumber: '',
          routingNumber: '',
          country: countryOptions[0].value,
          currency: currencyOptions[0].value
        })
      })
      .catch(error => console.log(error))
  }
})(InnerForm)

export default BankAccountForm



// WEBPACK FOOTER //
// ./src/components/organizations/create/bankaccount/BankAccountForm.tsx